import { create } from 'zustand';
import apiClient from '../lib/api';

export interface AdminStats {
  users: {
    total: number;
    active: number;
    mfa_active: number;
    pending: number;
    no_account?: number;
  };
  roles: {
    total: number;
    system: number;
    custom: number;
  };
  modules: {
    total: number;
    platform?: number;
    in_scope?: number;
  };
  permissions: {
    total: number;
    platform?: number;
    in_scope?: number;
  };
  audit: {
    total: number;
    failed_24h: number;
    lockouts: number;
    recent_perm_changes?: number;
  };
}

export interface AuditLogItem {
  id: string;
  tenant_id: string;
  user_id: string;
  action: string;
  resource_type: string;
  resource_id?: string;
  ip_address?: string;
  user_agent?: string;
  metadata?: any;
  changes?: {
    before?: any;
    after?: any;
  };
  created_at: string;
  user_email?: string;
  first_name?: string;
  last_name?: string;
}

export interface AdminModuleItem {
  id: string;
  name: string;
  icon: string;
  status: string;
  display_order: number;
  code: string;
  permissions_count: number;
  isPlatformManaged?: boolean;
  isInScope?: boolean;
  readOnly?: boolean;
  scopeLabel?: string;
  resources: Array<{
    id: string;
    name: string;
    permissionsCount: number;
    permissions?: Array<{
      id: string;
      action: string;
      name: string;
      code: string;
      description?: string;
    }>;
  }>;
}

export interface AdminRoleItem {
  id: string;
  name: string;
  description: string;
  is_system: boolean;
  code: string;
  category?: string;
  created_at: string;
  user_count: number;
  permissions_count: number;
  level?: number;
  service?: string;
  isPlatformRole?: boolean;
  isInScope?: boolean;
  permissions: Array<{
    id: string;
    action: string;
    resource: string;
    module: string;
  }>;
}

export interface AdminUserItem {
  id: string;
  user_id?: string;
  employee_id?: string;
  email: string;
  first_name: string;
  last_name: string;
  status: string;
  mfa_enabled: boolean;
  tenant_id: string;
  tenant_name: string;
  employee_code?: string;
  department?: string;
  designation?: string;
  has_account?: boolean;
  is_service_user?: boolean;
  permissions_count: number;
  roles: Array<{
    id: string;
    name: string;
    code?: string;
    category?: string;
  }>;
  rolesByService?: {
    support?: any[];
    asset?: any[];
    enduser?: any[];
    admin?: any[];
    global?: any[];
  };
}

export interface ServiceAccessItem {
  key: string;
  name: string;
  icon: string;
  delegationEnabled: boolean;
  adminCount: number;
  managerCount: number;
  agentCount: number;
  totalUsers: number;
}

export interface SystemHealthCheck {
  id: string;
  name: string;
  category: string;
  status: 'healthy' | 'degraded' | 'down';
  latencyMs?: number;
  uptimeSeconds?: number;
  memoryMb?: number;
  details?: string;
}

export interface SystemHealthData {
  status: string;
  overallText: string;
  timestamp: string;
  checks: SystemHealthCheck[];
}

export interface SsoConfig {
  provider: string;
  status: 'active' | 'enforced' | 'disabled';
  issuer: string;
  clientId: string;
  clientSecret?: string;
  metadataUrl: string;
  callbackUrl: string;
  allowedDomains: string[];
  autoProvision: boolean;
  defaultRole: string;
  signingAlgorithm: 'RS256' | 'HS256';
  enforceForAdmins: boolean;
  jitEnabled: boolean;
  certFingerprint?: string;
  updatedAt?: string;
}

export interface SecurityPolicy {
  mfaEnforcement: 'all' | 'admins_only' | 'optional';
  allowedMfaMethods: string[];
  sessionIdleTimeoutMins: number;
  maxConcurrentSessions: number;
  lockoutThreshold: number;
  lockoutDurationMins: number;
  minPasswordLength: number;
  requireSpecialChar: boolean;
  requireNumbers: boolean;
  requireUppercase: boolean;
  passwordMaxAgeDays: number;
  ipAllowlist: string[];
  blockTorAndProxies: boolean;
  updatedAt?: string;
}

export interface ApiKeyItem {
  id: string;
  name: string;
  prefix: string;
  keyMasked: string;
  scope: string[];
  tenantId: string;
  createdBy: string;
  createdAt: string;
  lastUsedAt: string | null;
  status: 'active' | 'revoked';
}

export interface OrganizationProfile {
  legalName?: string;
  domain?: string;
  industry?: string;
  email?: string;
  phone?: string;
  line1?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  defaultCurrency?: string;
  defaultTimezone?: string;
  defaultLanguage?: string;
  fiscalYearStart?: string;
}

export interface OrganizationDetails {
  id: string;
  name: string;
  code?: string;
  tenant_code?: string;
  slug?: string;
  settings?: {
    profile?: OrganizationProfile;
    branding?: Record<string, any>;
    [key: string]: any;
  };
}

interface AdminState {
  ssoConfig: SsoConfig | null;
  securityPolicy: SecurityPolicy | null;
  stats: AdminStats | null;
  auditLogs: AuditLogItem[];
  auditTotal: number;
  modules: AdminModuleItem[];
  roles: AdminRoleItem[];
  users: AdminUserItem[];
  serviceAccess: ServiceAccessItem[];
  systemHealth: SystemHealthData | null;
  apiKeys: ApiKeyItem[];
  organization: OrganizationDetails | null;
  isLoading: boolean;
  error: string | null;

  loadStats: () => Promise<void>;
  loadAuditLogs: (params?: { search?: string; category?: string; type?: string; limit?: number; offset?: number }) => Promise<void>;
  loadModules: () => Promise<void>;
  loadRoles: () => Promise<void>;
  loadUsers: (params?: { search?: string; department?: string; service?: string; role?: string; status?: string }) => Promise<void>;
  loadServiceAccess: () => Promise<void>;
  loadSystemHealth: () => Promise<void>;
  loadApiKeys: () => Promise<void>;
  loadOrganization: () => Promise<void>;
  updateOrganization: (data: { name?: string; profile?: Record<string, any>; branding?: Record<string, any> }) => Promise<OrganizationDetails>;
  createApiKey: (data: { name: string; scope: string[] }) => Promise<{ key: string }>;
  revokeApiKey: (id: string) => Promise<void>;
  assignUserRoles: (userId: string, roleIds: string[]) => Promise<{ success: boolean; message: string }>;
  inviteEmployee: (employeeId: string) => Promise<{ success: boolean; message: string }>;
  createRole: (data: { name: string; description: string; permissionIds?: string[] }) => Promise<AdminRoleItem>;
  updateRole: (id: string, data: { name?: string; description?: string; permissionIds?: string[] }) => Promise<AdminRoleItem>;
  batchUpdateMatrix: (changes: Array<{ roleId: string; serviceKey: string; actionId: string; granted: boolean }>) => Promise<{ success: boolean; appliedCount: number }>;
  deleteRole: (id: string) => Promise<void>;
  createModule: (data: { name: string; icon?: string }) => Promise<AdminModuleItem>;
  toggleUserMfa: (userId: string, enabled: boolean) => Promise<void>;
  updateUser: (id: string, data: Partial<AdminUserItem>) => Promise<void>;
  deleteUser: (id: string) => Promise<void>;
  duplicateUser: (id: string) => Promise<void>;
  toggleUserStatus: (id: string) => Promise<void>;
  resetUserMfa: (id: string) => Promise<void>;
  toggleServiceStatus: (userId: string, isServiceUser: boolean) => Promise<{ success: boolean; message: string }>;
  loadSsoConfig: () => Promise<void>;
  updateSsoConfig: (config: Partial<SsoConfig>) => Promise<void>;
  loadSecurityPolicy: () => Promise<void>;
  updateSecurityPolicy: (policy: Partial<SecurityPolicy>) => Promise<void>;
}

export const useAdminStore = create<AdminState>((set, get) => ({
  ssoConfig: null,
  securityPolicy: null,
  stats: null,
  auditLogs: [],
  auditTotal: 0,
  modules: [],
  roles: [],
  users: [],
  serviceAccess: [],
  systemHealth: null,
  apiKeys: [],
  organization: null,
  isLoading: false,
  error: null,

  loadStats: async () => {
    try {
      const res = await apiClient.get('/admin/stats').catch(() => apiClient.get('/auth/admin/stats'));
      const raw = res?.data?.data ?? res?.data;
      if (raw && typeof raw === 'object' && raw.users && raw.roles) {
        set({ stats: raw });
      } else {
        set({ stats: null });
      }
    } catch (err: any) {
      console.error('Failed to load admin stats:', err);
      set({ stats: null });
    }
  },

  loadAuditLogs: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const queryParams = { limit: 500, ...params };
      const res = await apiClient.get('/admin/audit-logs', { params: queryParams }).catch(() => apiClient.get('/auth/admin/audit-logs', { params: queryParams }));
      const raw = res.data?.data ?? res.data;
      set({
        auditLogs: Array.isArray(raw?.logs) ? raw.logs : (Array.isArray(raw) ? raw : []),
        auditTotal: typeof raw?.total === 'number' ? raw.total : (Array.isArray(raw) ? raw.length : 0),
        isLoading: false,
      });
    } catch (err: any) {
      set({ isLoading: false, error: err.message });
      console.error('Failed to load audit logs:', err);
    }
  },

  loadModules: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await apiClient.get('/admin/modules').catch(() => apiClient.get('/auth/admin/modules'));
      const raw = res.data?.data ?? res.data;
      set({ modules: Array.isArray(raw) ? raw : (raw?.modules || []), isLoading: false });
    } catch (err: any) {
      set({ isLoading: false, error: err.message });
      console.error('Failed to load modules:', err);
    }
  },

  loadRoles: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await apiClient.get('/admin/roles').catch(() => apiClient.get('/auth/admin/roles'));
      const raw = res.data?.data ?? res.data;
      set({ roles: Array.isArray(raw) ? raw : (raw?.roles || []), isLoading: false });
    } catch (err: any) {
      set({ isLoading: false, error: err.message });
      console.error('Failed to load roles:', err);
    }
  },

  loadUsers: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const res = await apiClient.get('/admin/users', { params }).catch(() => apiClient.get('/auth/admin/users', { params }));
      const raw = res.data?.data ?? res.data;
      const userList = Array.isArray(raw) ? raw : (raw?.users || raw?.data || []);
      set({ users: userList, isLoading: false });
    } catch (err: any) {
      set({ isLoading: false, error: err.message, users: [] });
      console.error('Failed to load admin users:', err);
    }
  },

  loadServiceAccess: async () => {
    try {
      const res = await apiClient.get('/admin/service-access').catch(() => apiClient.get('/auth/admin/service-access'));
      const raw = res.data?.data ?? res.data;
      set({ serviceAccess: Array.isArray(raw) ? raw : [] });
    } catch (err: any) {
      console.error('Failed to load service access:', err);
    }
  },

  loadSystemHealth: async () => {
    try {
      const res = await apiClient.get('/admin/system/health').catch(() => apiClient.get('/auth/admin/system/health'));
      const raw = res.data?.data ?? res.data;
      set({ systemHealth: raw ?? null });
    } catch (err: any) {
      console.error('Failed to load system health:', err);
    }
  },

  loadApiKeys: async () => {
    try {
      const res = await apiClient.get('/admin/api-keys').catch(() => apiClient.get('/auth/admin/api-keys'));
      const raw = res.data?.data ?? res.data;
      set({ apiKeys: Array.isArray(raw?.keys) ? raw.keys : (Array.isArray(raw) ? raw : []) });
    } catch (err: any) {
      console.error('Failed to load api keys:', err);
    }
  },

  loadOrganization: async () => {
    try {
      const res = await apiClient.get('/admin/organization').catch(() => apiClient.get('/auth/admin/organization'));
      const raw = res.data?.data ?? res.data;
      set({ organization: raw ?? null });
    } catch (err: any) {
      console.error('Failed to load organization:', err);
    }
  },

  updateOrganization: async (data) => {
    const res = await apiClient
      .patch('/admin/organization', data)
      .catch(() => apiClient.patch('/auth/admin/organization', data));
    const raw = res.data?.data ?? res.data;
    set({ organization: raw ?? null });
    return raw;
  },

  createApiKey: async (data) => {
    const res = await apiClient.post('/admin/api-keys', data).catch(() => apiClient.post('/auth/admin/api-keys', data));
    await get().loadApiKeys();
    return res.data;
  },

  revokeApiKey: async (id) => {
    await apiClient.delete(`/admin/api-keys/${id}`).catch(() => apiClient.delete(`/auth/admin/api-keys/${id}`));
    await get().loadApiKeys();
  },

  assignUserRoles: async (userId, roleIds) => {
    const res = await apiClient.post(`/admin/users/${userId}/roles`, { roleIds }).catch(() => apiClient.post(`/auth/admin/users/${userId}/roles`, { roleIds }));
    await get().loadUsers();
    await get().loadStats();
    await get().loadServiceAccess();
    return res.data;
  },

  inviteEmployee: async (employeeId) => {
    const res = await apiClient.post(`/admin/users/${employeeId}/invite`).catch(() => apiClient.post(`/auth/admin/users/${employeeId}/invite`));
    await get().loadUsers();
    await get().loadStats();
    return res.data;
  },

  createRole: async (data) => {
    const res = await apiClient.post('/admin/roles', data).catch(() => apiClient.post('/auth/admin/roles', data));
    await get().loadRoles();
    await get().loadStats();
    return res.data;
  },

  updateRole: async (id, data) => {
    const res = await apiClient.put(`/admin/roles/${id}`, data).catch(() => apiClient.put(`/auth/admin/roles/${id}`, data));
    await get().loadRoles();
    return res.data;
  },

  batchUpdateMatrix: async (changes) => {
    const res = await apiClient.post('/admin/roles/matrix/batch-update', { changes }).catch(() => apiClient.post('/auth/admin/roles/matrix/batch-update', { changes }));
    await get().loadRoles();
    return res.data;
  },

  deleteRole: async (id) => {
    await apiClient.delete(`/admin/roles/${id}`).catch(() => apiClient.delete(`/auth/admin/roles/${id}`));
    await get().loadRoles();
    await get().loadStats();
  },

  createModule: async (data) => {
    const res = await apiClient.post('/admin/modules', data).catch(() => apiClient.post('/auth/admin/modules', data));
    await get().loadModules();
    await get().loadStats();
    return res.data;
  },

  toggleUserMfa: async (userId, enabled) => {
    await apiClient.patch(`/auth/admin/users/${userId}/mfa`, { enabled });
    set((state) => ({
      users: state.users.map(u => (u.id === userId || u.user_id === userId) ? { ...u, mfa_enabled: enabled } : u),
    }));
    await get().loadStats();
  },

  updateUser: async (id, data) => {
    await apiClient.patch(`/auth/users/${id}`, {
      email: data.email,
      name: [data.first_name, data.last_name].filter(Boolean).join(' ') || undefined,
      status: data.status,
    });
    await get().loadUsers();
    await get().loadStats();
  },

  deleteUser: async (id) => {
    await apiClient.delete(`/auth/users/${id}`);
    set((state) => ({ users: state.users.filter(u => u.id !== id && u.user_id !== id) }));
    await get().loadStats();
  },

  duplicateUser: async () => {
    throw new Error('Create a user with a unique email through the invitation flow');
  },

  toggleUserStatus: async (id) => {
    const target = get().users.find(u => u.id === id || u.user_id === id);
    if (!target) throw new Error('User not found');
    const status = target.status === 'active' ? 'inactive' : 'active';
    await get().updateUser(id, { status });
  },

  resetUserMfa: async (id) => {
    await get().toggleUserMfa(id, false);
  },

  toggleServiceStatus: async (userId, isServiceUser) => {
    const res = await apiClient.post(`/admin/users/${userId}/service-status`, { isServiceUser })
      .catch(() => apiClient.post(`/auth/admin/users/${userId}/service-status`, { isServiceUser }));
    const data = res.data?.data ?? res.data;
    set((state) => ({
      users: state.users.map(u => 
        (u.id === userId || u.user_id === userId) 
          ? { ...u, is_service_user: isServiceUser } 
          : u
      ),
    }));
    await get().loadStats();
    return { success: true, message: data?.message || 'Service account status updated' };
  },

  loadSsoConfig: async () => {
    try {
      const res = await apiClient.get('/admin/security/sso').catch(() => apiClient.get('/auth/admin/security/sso'));
      const raw = res.data?.data ?? res.data;
      set({ ssoConfig: raw ?? null });
    } catch (err: any) {
      console.error('Failed to load SSO config:', err);
    }
  },

  updateSsoConfig: async (config: Partial<SsoConfig>) => {
    const res = await apiClient.put('/admin/security/sso', config).catch(() => apiClient.put('/auth/admin/security/sso', config));
    const raw = res.data?.data ?? res.data;
    set({ ssoConfig: raw });
  },

  loadSecurityPolicy: async () => {
    try {
      const res = await apiClient.get('/admin/security/policy').catch(() => apiClient.get('/auth/admin/security/policy'));
      const raw = res.data?.data ?? res.data;
      set({ securityPolicy: raw ?? null });
    } catch (err: any) {
      console.error('Failed to load security policy:', err);
    }
  },

  updateSecurityPolicy: async (policy: Partial<SecurityPolicy>) => {
    const res = await apiClient.put('/admin/security/policy', policy).catch(() => apiClient.put('/auth/admin/security/policy', policy));
    const raw = res.data?.data ?? res.data;
    set({ securityPolicy: raw });
  },
}));
